import React, { memo, useCallback, useMemo } from 'react';

import AddNewKpiAlertRule from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule';

import { useEditKpiAlert } from '@hooks/kpiAlerts/useEditKpiAlert';
import { useKPIAlert } from '@hooks/kpiAlerts/useKPIAlert';
import { useKPI } from '@hooks/kpis/useKPI';
import { useQueryKpiGroup } from '@hooks/kpis/useQueryKpiGroup';
import { useQueryKpiSettings } from '@hooks/kpis/useQueryKpiSettings';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { normalizeObject } from '@utils/common';
import { addCheckedProperty } from '@components/routes/AdminPanel/KpiAlertsManagement/utils';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IKpiAlertSchema } from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/interfaces';

import { ESnackbarTypes, TVendorsValues } from '@enums/common';
import { EKpiAlertsStatus } from '@enums/kpi-common';

import { responseMessages } from '@constants/responseMessages';

function EditKpiAlertRule({
	id,
	closeModal,
	setIsDirtyForm,
	closeModalWithoutConfirm,
	vendor,
}: {
	id: string;
	closeModal: () => void;
	closeModalWithoutConfirm: () => void;
	setIsDirtyForm: (isDirty: boolean) => void;
	vendor: TSelectOption<TVendorsValues>;
}) {
	const dispatch = useAppDispatch();

	const onError = () => {
		dispatch(setSnackbar({
			type: ESnackbarTypes.error,
			message: responseMessages.error,
		}));

		closeModal();
	};

	const {
		kpiAlertRule,
		formattedKpiAlertRule,
		isLoading,
		refetchKpiAlertRule,
	} = useKPIAlert({ id, onError, vendor: vendor?.value });

	const onEditSuccess = () => {
		refetchKpiAlertRule();

		dispatch(setSnackbar({
			type: ESnackbarTypes.success,
			message: `The ${kpiAlertRule?.name} has been changed!`,
		}));

		closeModalWithoutConfirm();
	};

	const onEditError = () => {
		dispatch(setSnackbar({
			type: ESnackbarTypes.error,
			message: responseMessages.error,
		}));

		closeModal();
	};

	const {
		editKpiAlert,
		isLoadingEditKpiAlert,
	} = useEditKpiAlert({
		onSuccess: onEditSuccess,
		onError: onEditError,
		vendor: vendor?.value,
	});

	const handleEditRule = useCallback((formValues: IKpiAlertSchema) => {
		if (!kpiAlertRule) return;

		editKpiAlert({
			...normalizeObject({
				id: kpiAlertRule.id,
				isActive: formValues.status === EKpiAlertsStatus.active,
				name: formValues.ruleName,
				severity: formValues.severity,
				roles: formValues.roles?.map((role: any) => role.value),
				users: formValues.users?.map((user) => user?.id) as string[],
			}),
			description: formValues.ruleDescription,
		});
	}, [editKpiAlert, kpiAlertRule]);

	const {
		options5GOran,
	} = useKPI();

	const { maxSelection } = useQueryKpiSettings(vendor?.value);

	const { kpiGroupData } = useQueryKpiGroup({
		vendor: vendor?.value as TVendorsValues,
		isWithMaxSelection: !!maxSelection,
		isLineType: true,
	});

	const formDefaultValues = useMemo(() => {
		const hasRequiredData = options5GOran && kpiAlertRule && formattedKpiAlertRule && kpiGroupData;

		let defaultValues = formattedKpiAlertRule;

		if (hasRequiredData && !formattedKpiAlertRule.sitesT_5G) {
			const baseFormValues = { ...formattedKpiAlertRule };
			const checked5GSites = addCheckedProperty(options5GOran);

			baseFormValues.sitesT_5G = checked5GSites;

			defaultValues = baseFormValues;
		}

		if (hasRequiredData && defaultValues?.kpi?.length === 0) {
			const selectedKpiData = kpiGroupData.map((kpi) => {
				return {
					...kpi,
					isChecked: !!kpiAlertRule.kpis.find((selectedKpi) => selectedKpi.id === kpi.dbId),
				};
			});

			defaultValues.kpi = selectedKpiData as any;
		}

		if (hasRequiredData && defaultValues && !defaultValues?.aggregationType) {
			defaultValues.aggregationType = kpiAlertRule?.aggregationType.name;
		}

		return defaultValues;
	}, [options5GOran, kpiAlertRule, formattedKpiAlertRule, kpiGroupData]);

	return (
		<>
			<AddNewKpiAlertRule
				closeModal={closeModal}
				defaultValues={formDefaultValues}
				handleEditRule={handleEditRule}
				isEdit
				isLoading={isLoading || isLoadingEditKpiAlert}
				negativeBtnText='Cancel'
				positiveBtnText='Save'
				setIsDirtyForm={setIsDirtyForm}
			/>
		</>
	);
}

export default memo(EditKpiAlertRule);
